import { getApiGalaxyServer, postApiGalaxyServer } from 'services';
import { Realm } from 'types';
import { Tenant } from 'TenantModule/types';

/** A `pass`/`fail` is a proven state; `warn`/`unknown` mean galaxy could not verify it. */
export type PreflightCheckResult = 'pass' | 'fail' | 'warn' | 'unknown';

export interface PreflightCheck {
  name: string;
  result: PreflightCheckResult;
  detail: string;
}

export interface PreflightServicePlan {
  service: string;
  registeredOnTarget: PreflightCheckResult;
  targetCredentials: PreflightCheckResult;
  sourceCredentials: PreflightCheckResult;
  sourceSizeBytes: number | null;
  sizingTier: string | null;
  detail: string;
}

export interface MigrationPreflightReport {
  ok: boolean;
  checks: PreflightCheck[];
  servicesToMigrate: PreflightServicePlan[];
  /** Plain-language description of every database whose schema this migration destroys. */
  destructive: string[];
  estimate: { longestServiceTier: string | null; note: string };
}

export interface RealmMigrationSummary {
  migrationLogId: number | null;
  taskId: number | null;
  tenant: string | null;
  sourceRealmId: string | null;
  targetRealmId: string | null;
  teamIds: string | null;
  status: string;
  initiatedBy: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMinutes: number | null;
  totalSteps: number;
  failedSteps: number;
  taskStatus: string | null;
  traceId: string | null;
  /**
   * False for runs started through `migrateTeamDataToRealmDB`, which create no recovery
   * record — their status is best-effort, so the UI must not present it as authoritative.
   */
  tracked: boolean;
}

export interface RealmMigrationStep {
  id: number;
  action: string;
  service: string | null;
  status: string | null;
  severity: string | null;
  triggeredAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
  traceId: string | null;
  isRoot: boolean;
}

export interface MigrationCoordinates {
  teamIds: string;
  sourceRealmId: Realm['realm_id'];
  targetRealmId: Realm['realm_id'];
  sourceTenant: Tenant['tenantName'];
  targetTenant: Tenant['tenantName'];
  migrateArchivedIntegrations?: boolean;
}

export interface MigrationPreflightPayload extends MigrationCoordinates {
  /**
   * Opt-in. The size probe opens a real database connection per planned service, so the
   * form leaves it off while the operator is still typing and asks for it once before
   * submitting.
   */
  includeSizeEstimate?: boolean;
}

export type RunRealmMigrationPayload = MigrationCoordinates;

/**
 * `runRealmMigration` refuses more than one team by returning a 201 whose body carries
 * only `message` — no `status` — so a caller that branches on the HTTP status reports a
 * migration as started when nothing ran. The UI has to read the body.
 */
export interface RunRealmMigrationResponse {
  status?: number;
  message: string;
}

export const migrationPreflight = ({
  params: data,
  token,
  config = {},
}: BaseApiParams<MigrationPreflightPayload>) =>
  postApiGalaxyServer<MigrationPreflightPayload, MigrationPreflightReport>(
    '/realm/migrationPreflight',
    data,
    config,
    token,
  );

export const runRealmMigration = ({
  params: data,
  token,
  config = {},
}: BaseApiParams<RunRealmMigrationPayload>) =>
  postApiGalaxyServer<RunRealmMigrationPayload, RunRealmMigrationResponse>(
    '/realm/runRealmMigration',
    data,
    config,
    token,
  );

export const listRealmMigrations = ({
  params,
  token,
  config = {},
}: BaseApiParams<{ realmId?: string; teamId?: string; limit?: number } | undefined>) => {
  const query = new URLSearchParams();
  if (params?.realmId) query.set('realmId', params.realmId);
  if (params?.teamId) query.set('teamId', params.teamId);
  if (params?.limit !== undefined) query.set('limit', String(params.limit));
  const qs = query.toString();
  const path = qs ? `/realm/migrations?${qs}` : '/realm/migrations';

  return getApiGalaxyServer<RealmMigrationSummary[]>(path, config, token);
};

export const getRealmMigrationSteps = ({
  params,
  token,
  config = {},
}: BaseApiParams<{ migrationLogId: number }>) =>
  getApiGalaxyServer<RealmMigrationStep[]>(
    `/realm/migrations/${params.migrationLogId}/steps`,
    config,
    token,
  );
